import React from 'react';
import {
  Box,
  Paper,
  TextField,
  Grid,
  Button,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

export interface FilterField {
  name: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'year';
  options?: Array<{ value: string | number; label: string }>;
  value?: any;
  placeholder?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  gridSize?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  };
}

interface ListFiltersProps {
  fields: FilterField[];
  onFilterChange: (name: string, value: any) => void;
  onSearch: () => void;
  onClear: () => void;
  isLoading?: boolean;
}

export default function ListFilters({
  fields,
  onFilterChange,
  onSearch,
  onClear,
  isLoading = false,
}: ListFiltersProps) {
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        backgroundColor: 'background.paper',
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 3,
        }}
      >
        <FilterListIcon color="primary" />
        <Box
          component="h3"
          sx={{
            m: 0,
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          Filtros de Pesquisa
        </Box>
      </Box>

      <Grid container spacing={3}>
        {fields.map((field) => (
          <Grid
            item
            xs={field.gridSize?.xs || 12}
            sm={field.gridSize?.sm || (field.fullWidth ? 12 : 6)}
            md={field.gridSize?.md || (field.fullWidth ? 12 : field.type === 'text' ? 6 : 3)}
            lg={field.gridSize?.lg}
            key={field.name}
          >
            {field.type === 'select' ? (
              <TextField
                select
                fullWidth
                label={field.label}
                value={field.value || ''}
                onChange={(e) => onFilterChange(field.name, e.target.value)}
                variant="outlined"
                size="medium"
                disabled={isLoading || field.disabled}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {field.options?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : field.type === 'year' ? (
              <TextField
                fullWidth
                label={field.label}
                value={field.value || ''}
                onChange={(e) => onFilterChange(field.name, e.target.value)}
                onKeyPress={handleKeyPress}
                variant="outlined"
                size="medium"
                type="number"
                placeholder={field.placeholder || 'AAAA'}
                disabled={isLoading}
                inputProps={{
                  min: 2000,
                  max: new Date().getFullYear() + 1,
                }}
              />
            ) : field.type === 'date' ? (
              <TextField
                fullWidth
                label={field.label}
                value={field.value || ''}
                onChange={(e) => onFilterChange(field.name, e.target.value)}
                variant="outlined"
                size="medium"
                type="date"
                disabled={isLoading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            ) : (
              <TextField
                fullWidth
                label={field.label}
                value={field.value || ''}
                onChange={(e) => onFilterChange(field.name, e.target.value)}
                onKeyPress={handleKeyPress}
                variant="outlined"
                size="medium"
                placeholder={field.placeholder}
                disabled={isLoading}
                InputProps={
                  field.name === 'term'
                    ? {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            )}
          </Grid>
        ))}

        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={onClear}
              disabled={isLoading}
              size="large"
              sx={{
                minWidth: 140,
                py: 1.5,
              }}
            >
              Limpar
            </Button>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={onSearch}
              disabled={isLoading}
              size="large"
              sx={{
                minWidth: 140,
                py: 1.5,
              }}
            >
              Buscar
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}
